import React, { useMemo } from "react";
import moment from "moment";
import { convertToTitleCase } from "../../../utils/helpers";
import {
  groupTimelineByDate,
  normalizeTimelineItems,
  toneColor,
} from "../../../utils/timeline";
import "./unifiedTimeline.css";

function TimelineSkeleton({ rows = 4 }) {
  return (
    <ul className="unified-timeline list-unstyled mb-0" aria-busy="true" aria-label="Loading timeline">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="unified-timeline__item unified-timeline__item--skeleton">
          <div className="unified-timeline__dot unified-timeline__dot--skeleton" />
          <div className="unified-timeline__body">
            <div className="unified-timeline__skel unified-timeline__skel--title" />
            <div className="unified-timeline__skel unified-timeline__skel--line" />
            <div className="unified-timeline__skel unified-timeline__skel--meta" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function TimelineEmptyState({ message = "You're all caught up on activity." }) {
  return (
    <div className="unified-timeline__empty" role="status">
      <p className="mb-0 small text-muted">{message}</p>
    </div>
  );
}

/**
 * Shared vertical timeline for investment, marketplace, real estate, and KYC.
 */
export default function UnifiedTimeline({
  items = [],
  loading = false,
  error = null,
  compact = false,
  groupByDate = false,
  showActor = true,
  emptyMessage = "No timeline events yet.",
  className = "",
}) {
  const normalized = useMemo(() => normalizeTimelineItems(items), [items]);

  const sorted = useMemo(
    () =>
      [...normalized].sort(
        (a, b) => new Date(b.occurredAt || 0) - new Date(a.occurredAt || 0)
      ),
    [normalized]
  );

  const groups = useMemo(
    () => (groupByDate ? groupTimelineByDate(sorted) : [{ key: "all", label: null, items: sorted }]),
    [groupByDate, sorted]
  );

  if (loading) return <TimelineSkeleton />;
  if (error) {
    return (
      <div className="unified-timeline__error" role="alert">
        <p className="mb-0 small text-danger">
          {typeof error === "string" ? error : "Could not load timeline."}
        </p>
      </div>
    );
  }
  if (!sorted.length) return <TimelineEmptyState message={emptyMessage} />;

  return (
    <div className={`unified-timeline-wrap ${className}`.trim()}>
      {groups.map((group) => (
        <div key={group.key} className="unified-timeline__group">
          {group.label ? (
            <p className="unified-timeline__group-label">{group.label}</p>
          ) : null}
          <ul className={`unified-timeline list-unstyled mb-0 ${compact ? "unified-timeline--compact" : ""}`}>
            {group.items.map((item) => {
              const color = toneColor(item.tone);
              const title =
                item.title ||
                convertToTitleCase(String(item.status || "Update").replace(/_/g, " "));
              const when = item.occurredAt
                ? moment(item.occurredAt).format("DD-MMM-YYYY HH:mm")
                : "";
              const actorLabel = showActor ? item.actor?.name : null;

              return (
                <li key={item.id} className="unified-timeline__item">
                  <div
                    className="unified-timeline__dot"
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                  />
                  <div className="unified-timeline__body">
                    <p className="unified-timeline__title mb-0">{title}</p>
                    {!compact && item.description ? (
                      <p className="unified-timeline__desc mb-1">{item.description}</p>
                    ) : null}
                    <p className="unified-timeline__meta mb-0">
                      {[when, actorLabel].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

export { TimelineSkeleton, TimelineEmptyState };
